import React, { useState } from 'react';
import { Sparkles, Info, MapPin } from 'lucide-react';
import { TermHelp } from './AgentHelp';

/* The Kitxens Effect — shared showcase card: demo OS, admin AI OS and affiliate dashboard. */

const money = (v) => `$${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export const EFFECT_PERIODS = [
  ['today', 'Hoy', 'Today'],
  ['yesterday', 'Ayer', 'Yesterday'],
  ['week', 'Semana', 'Week'],
  ['month', 'Mes', 'Month'],
  ['year', 'Año', 'Year'],
  ['lifetime', 'Histórico', 'Lifetime'],
];

const PHRASE = {
  today: ['extra hoy', 'extra today'],
  yesterday: ['extra ayer', 'extra yesterday'],
  week: ['extra en 7 días', 'extra in 7 days'],
  month: ['extra en 30 días', 'extra in 30 days'],
  year: ['extra en 12 meses', 'extra in 12 months'],
  lifetime: ['extra en todo el histórico', 'extra lifetime'],
};

const SIM_PHRASE = {
  today: ['al día', 'per day'],
  yesterday: ['al día', 'per day'],
  week: ['en 7 días', 'in 7 days'],
  month: ['al mes', 'per month'],
  year: ['en 12 meses', 'in 12 months'],
  lifetime: ['al mes', 'per month'],
};

const BREAKDOWN_LABEL = {
  direct: ['Marketing directo', 'Direct marketing'],
  reviews: ['Reseñas y reputación', 'Reviews & reputation'],
  delivery: ['Delivery y pedidos online', 'Delivery & online orders'],
  repeat: ['Clientes que repiten', 'Repeat guests'],
};

const METHOD_LINES = [
  ['"Sin Kitxens" proyecta el promedio diario de tus primeras semanas de datos — tu ritmo antes de activar Kitxens.',
    '"Without Kitxens" projects the daily average of your first weeks of data — your pace before activating Kitxens.'],
  ['"Con Kitxens" son tus ventas reales registradas en el periodo. El efecto es simplemente la diferencia.',
    '"With Kitxens" is your real recorded sales for the period. The effect is simply the difference.'],
  ['Doble tope, nunca prometemos más: máximo ~10% de incremento neto anual (la media del sector por marketing digital es ~9.9%) y máximo ~$300/día, ~$7.000/mes y ~$130.000/año extra por local.',
    'Double cap, we never promise more: max ~10% net annual uplift (industry average for digital marketing is ~9.9%) and max ~$300/day, ~$7,000/month and ~$130,000/year extra per location.'],
  ['La proyección madura de forma comedida: ~8% neto el Año 1, ~9% el Año 2 y ~10% el Año 3 — siempre lo que ocurra primero entre el porcentaje y el tope en dólares.',
    'The projection matures conservatively: ~8% net in Year 1, ~9% in Year 2 and ~10% in Year 3 — always whichever is lower between the percentage and the dollar cap.'],
  ['El simulador asume un retorno publicitario ~4x con rendimiento decreciente, con tope del 3% de tu facturación. El neto descuenta costes de producto, gastos variables y el propio presupuesto.',
    'The simulator assumes a ~4x diminishing ad return, capped at 3% of your revenue. The net line deducts product costs, variable expenses and the budget itself.'],
  ['Todo son estimaciones sobre tus datos importados; no constituyen garantía de resultados.',
    'Everything is an estimate based on your imported data; it is not a guarantee of results.'],
];

function MethodologyNote({ isEs }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 rounded-xl border border-gray-200 overflow-hidden" data-testid="kx-methodology">
      <button onClick={() => setOpen(!open)} data-testid="kx-methodology-toggle"
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 bg-gray-50/60 hover:bg-gray-100/70 transition-colors text-left">
        <span className="flex items-center gap-1.5 text-[12px] font-bold text-gray-600">
          <Info className="w-3.5 h-3.5 text-[var(--rest-accent,#F77F11)]" />
          {isEs ? '¿Cómo lo calculamos?' : 'How do we calculate this?'}
        </span>
        <span className={`text-gray-400 text-[11px] transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {open && (
        <ul className="px-4 py-3 space-y-2 bg-white" data-testid="kx-methodology-body">
          {METHOD_LINES.map((l, i) => (
            <li key={i} className="flex gap-2 text-[11.5px] leading-relaxed text-gray-500">
              <span className="text-[var(--rest-accent,#F77F11)] font-bold shrink-0">·</span>
              <span>{l[isEs ? 0 : 1]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function EstimateDisclaimer({ isEs, testid = 'estimate-disclaimer' }) {
  return (
    <p className="mt-6 flex items-start gap-2 text-[10.5px] leading-relaxed text-gray-400 border-t border-gray-100 pt-3" data-testid={testid}>
      <Info className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
      <span>
        {isEs
          ? 'Todos los datos, comparativas y proyecciones mostrados son ESTIMACIONES aproximadas basadas en nuestra experiencia pasada y en modelos de comportamiento futuro. Son escenarios posibles, no promesas: Kitxens no garantiza ningún resultado concreto, aunque sí es posible que suceda. Cada negocio es distinto y los resultados reales pueden variar.'
          : 'All data, comparisons and projections shown are approximate ESTIMATES based on our past experience and future behavior models. They are possible scenarios, not promises: Kitxens does not guarantee any specific outcome, although it may well happen. Every business is different and actual results may vary.'}
      </span>
    </p>
  );
}

function BudgetSimulator({ d, period, isEs, bi, setBi }) {
  const scenarios = d.simulator?.scenarios || [];
  if (!scenarios.length) return null;
  const sel = bi >= 0 ? scenarios[Math.min(bi, scenarios.length - 1)] : null;
  const maxExtra = Math.max(...scenarios.map((s) => s.extra_period)) || 1;
  const [phEs, phEn] = SIM_PHRASE[period] || SIM_PHRASE.month;
  const ph = isEs ? phEs : phEn;
  return (
    <div className="mt-5 rounded-2xl border border-gray-200 bg-gray-50/60 p-4" data-testid="kx-budget-simulator">
      <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
        <Sparkles className="w-3.5 h-3.5 text-[var(--rest-accent,#F77F11)]" />
        {isEs ? '¿Y si sumas presupuesto a Kitxens?' : 'What if you add budget to Kitxens?'}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="kx-sim-budgets">
        <button onClick={() => setBi(-1)} data-testid="kx-sim-budget-0"
          className={`rounded-full px-3.5 py-1.5 text-[12px] font-bold tabular-nums transition-colors ${bi < 0
            ? 'bg-gray-900 text-white shadow-sm'
            : 'border border-gray-200 bg-white text-gray-500 hover:border-gray-400 hover:text-gray-800'}`}>
          {isEs ? 'Actual' : 'Current'}
        </button>
        {scenarios.map((s, i) => (
          <button key={s.budget} onClick={() => setBi(i)} data-testid={`kx-sim-budget-${s.budget}`}
            className={`rounded-full px-3.5 py-1.5 text-[12px] font-bold tabular-nums transition-colors ${i === bi
              ? 'bg-[var(--rest-accent,#F77F11)] text-white shadow-sm'
              : 'border border-gray-200 bg-white text-gray-500 hover:border-[var(--rest-accent,#F77F11)] hover:text-[var(--rest-accent,#F77F11)]'}`}>
            +${s.budget}<span className="font-semibold opacity-70">/{isEs ? 'mes' : 'mo'}</span>
          </button>
        ))}
      </div>
      <div className="mt-3.5">
        <div className="flex items-baseline justify-between flex-wrap gap-1">
          <p className="text-[13px] text-gray-600">
            {sel
              ? (isEs ? <>Con <b>+${sel.budget} USD/mes</b> de presupuesto, el retorno extra estimado es</> : <>With <b>+${sel.budget} USD/mo</b> of budget, the estimated extra return is</>)
              : (isEs ? <>Elige un presupuesto y verás todo el efecto Kitxens recalculado arriba al instante.</> : <>Pick a budget and the whole Kitxens effect above recalculates instantly.</>)}
          </p>
          {sel && (
            <p className="text-[17px] font-black tabular-nums text-[var(--rest-accent,#F77F11)]" data-testid="kx-sim-result">
              +{money(sel.extra_period)} <span className="text-[11.5px] font-bold text-gray-400">{isEs ? 'en ventas' : 'in sales'} {ph}</span>
            </p>
          )}
        </div>
        <div className="mt-1.5 h-3 rounded-full bg-gray-200/70 overflow-hidden">
          <div className="h-full rounded-full bg-[var(--rest-accent,#F77F11)] transition-[width] duration-500"
            style={{ width: sel ? `${Math.max(6, 100 * sel.extra_period / maxExtra)}%` : '0%' }} />
        </div>
        {sel && sel.net_monthly != null && (
          <p className="mt-2 text-[12px] font-semibold text-gray-600 tabular-nums" data-testid="kx-sim-net">
            {isEs
              ? <>Beneficio neto estimado (tras costes, gastos y el propio presupuesto): <b className={sel.net_monthly >= 0 ? 'text-emerald-600' : 'text-red-500'}>{sel.net_monthly >= 0 ? '+' : ''}{money(sel.net_monthly)}/mes</b></>
              : <>Estimated net profit (after costs, expenses and the budget itself): <b className={sel.net_monthly >= 0 ? 'text-emerald-600' : 'text-red-500'}>{sel.net_monthly >= 0 ? '+' : ''}{money(sel.net_monthly)}/mo</b></>}
          </p>
        )}
        {sel && sel.breakdown?.length > 0 && (
          <div className="mt-3 space-y-1.5" data-testid="kx-sim-breakdown">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-400">
              {isEs ? 'De dónde sale el retorno' : 'Where the return comes from'}
            </p>
            {sel.breakdown.map((bkd) => (
              <div key={bkd.key} className="flex items-center gap-2" data-testid={`kx-sim-breakdown-${bkd.key}`}>
                <span className="w-44 shrink-0 text-[11.5px] text-gray-600">{(BREAKDOWN_LABEL[bkd.key] || [bkd.key, bkd.key])[isEs ? 0 : 1]}</span>
                <div className="flex-1 h-2 rounded-full bg-gray-200/70 overflow-hidden">
                  <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${bkd.share * 100}%`, background: 'var(--rest-accent, #F77F11)', opacity: 0.85 }} />
                </div>
                <span className="w-24 text-right text-[11.5px] font-bold tabular-nums text-gray-700">+{money(bkd.monthly)}/{isEs ? 'mes' : 'mo'}</span>
              </div>
            ))}
          </div>
        )}
        {sel && (
          <p className="mt-1.5 text-[10.5px] text-gray-400">
            {period !== 'month' && period !== 'lifetime' && (isEs ? `≈ +${money(sel.extra_monthly)} en ventas al mes · ` : `≈ +${money(sel.extra_monthly)} in sales per month · `)}
            {sel.roas != null && <>{isEs ? `Retorno ~${sel.roas}x con rendimiento decreciente` : `~${sel.roas}x return with diminishing returns`} <TermHelp term="roas" isEs={isEs} /> · </>}
            {isEs ? 'Proporcional a tu facturación y perfil de cocina. Estimado, no garantizado.' : 'Proportional to your revenue and cuisine profile. Estimated, not guaranteed.'}
          </p>
        )}
      </div>
    </div>
  );
}

export function KitxensEffectCard({ d, period, setPeriod, isEs, title, subtitle, className = '', testid = 'kitxens-effect-card', locations, loc = '', setLoc }) {
  const [bi, setBi] = useState(-1);
  if (!d?.has_data) return null;
  const nLocs = d.scope?.n_locations || locations?.length || 1;
  const scopeTxt = loc
    ? (isEs ? `Solo local: ${loc}` : `Single location: ${loc}`)
    : (nLocs > 1 ? (isEs ? `Total de ${nLocs} locales` : `Total across ${nLocs} locations`) : (isEs ? 'Local único' : 'Single location'));
  const scenarios = d.simulator?.scenarios || [];
  const sel = bi >= 0 && scenarios.length ? scenarios[Math.min(bi, scenarios.length - 1)] : null;
  const boost = sel ? sel.extra_period : 0;
  const withTotal = d.with_kitxens + boost;
  const extraTotal = d.extra_revenue + boost;
  const uplift = d.without_kitxens > 0 ? Math.round(10 * 100 * (withTotal - d.without_kitxens) / d.without_kitxens) / 10 : d.uplift_pct;
  const woPct = withTotal > 0 ? Math.max(8, Math.round(100 * d.without_kitxens / withTotal)) : 8;
  const basePct = withTotal > 0 ? Math.round(1000 * d.with_kitxens / withTotal) / 10 : 100;
  const [phEs, phEn] = PHRASE[period] || PHRASE.year;
  return (
    <div className={`rounded-2xl bg-white border border-gray-200 shadow-sm p-6 relative overflow-hidden ${className}`} data-testid={testid}>
      <span className="absolute inset-x-0 top-0 h-[3px] bg-[var(--rest-accent,#F77F11)]" aria-hidden="true" />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--rest-accent,#F77F11)] font-bold">
            {title || (isEs ? 'El efecto Kitxens' : 'The Kitxens effect')}
          </p>
          {subtitle && <p className="mt-0.5 text-[11.5px] text-gray-400">{subtitle}</p>}
          <h3 className="mt-1 text-[19px] sm:text-[21px] font-bold text-gray-900" data-testid="kx-effect-headline">
            +{money(extraTotal)} {isEs ? phEs : phEn} <span className="text-[11.5px] font-semibold text-gray-400 align-middle">({isEs ? 'estimado' : 'estimated'})</span>
            {sel && (
              <span className="ml-2 align-middle inline-flex items-center rounded-full bg-[var(--rest-accent-soft,#FEF3E7)] border border-[var(--rest-accent,#F77F11)]/30 text-[var(--rest-accent,#F77F11)] text-[11px] font-bold px-2.5 py-0.5" data-testid="kx-effect-boost-badge">
                {isEs ? `incluye +${money(boost)} en ventas con +$${sel.budget}/mes` : `includes +${money(boost)} in sales with +$${sel.budget}/mo`}
              </span>
            )}
          </h3>
          {(nLocs > 1 || loc) && (
            <div className="mt-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 border border-gray-200 text-gray-600 text-[10.5px] font-bold px-2.5 py-0.5" data-testid="kx-effect-scope">
                <MapPin className="w-3 h-3" /> {scopeTxt}
              </span>
            </div>
          )}
          <p className="mt-1 text-[12.5px] text-gray-500 max-w-lg">
            {isEs
              ? `Al ritmo previo a Kitxens se habrían facturado ${money(d.without_kitxens)} en este periodo. Con Kitxens${sel ? ` y +$${sel.budget}/mes` : ''}: ${money(withTotal)} (estimado).`
              : `At the pre-Kitxens pace this period would have billed ${money(d.without_kitxens)}. With Kitxens${sel ? ` plus +$${sel.budget}/mo` : ''}: ${money(withTotal)} (estimated).`}
          </p>
        </div>
        {uplift != null && (
          <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold text-[13px] px-3.5 py-1.5 tabular-nums transition-colors" data-testid="kx-effect-uplift">
            ▲ +{uplift}% {isEs ? 'vs antes' : 'vs before'}
          </span>
        )}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="kx-effect-periods">
        {EFFECT_PERIODS.map(([k, es, en]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`kx-period-${k}`}
            className={`rounded-full px-3 py-1 text-[11.5px] font-bold transition-colors ${period === k
              ? 'bg-gray-900 text-white'
              : 'border border-gray-200 bg-white text-gray-500 hover:border-gray-400 hover:text-gray-800'}`}>
            {isEs ? es : en}
          </button>
        ))}
      </div>
      {locations?.length > 1 && setLoc && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="kx-effect-locations">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-bold text-gray-400 mr-0.5">
            <MapPin className="w-3 h-3" /> {isEs ? 'Local' : 'Location'}
          </span>
          <button onClick={() => setLoc('')} data-testid="kx-loc-all"
            className={`rounded-full px-3 py-1 text-[11px] font-bold transition-colors ${!loc
              ? 'bg-[var(--rest-accent,#F77F11)] text-white shadow-sm'
              : 'border border-gray-200 bg-white text-gray-500 hover:border-[var(--rest-accent,#F77F11)] hover:text-[var(--rest-accent,#F77F11)]'}`}>
            {isEs ? `Todos (${locations.length})` : `All (${locations.length})`}
          </button>
          {locations.map((n) => (
            <button key={n} onClick={() => setLoc(n)} data-testid={`kx-loc-${n.toLowerCase().replace(/ /g, '-')}`}
              className={`rounded-full px-3 py-1 text-[11px] font-bold transition-colors ${loc === n
                ? 'bg-[var(--rest-accent,#F77F11)] text-white shadow-sm'
                : 'border border-gray-200 bg-white text-gray-500 hover:border-[var(--rest-accent,#F77F11)] hover:text-[var(--rest-accent,#F77F11)]'}`}>
              {n}
            </button>
          ))}
        </div>
      )}
      <div className="mt-5 space-y-3">
        <div data-testid="kx-effect-with">
          <div className="flex items-center justify-between text-[12px] mb-1">
            <b className="text-gray-900">
              {isEs ? 'Con Kitxens' : 'With Kitxens'}
              {sel && <span className="text-[var(--rest-accent,#F77F11)]"> + ${sel.budget}/{isEs ? 'mes' : 'mo'}</span>}
            </b>
            <b className="tabular-nums text-[var(--rest-accent,#F77F11)]">{money(withTotal)}</b>
          </div>
          <div className="h-3.5 rounded-full bg-gray-100 overflow-hidden flex">
            <div className="h-full bg-[var(--rest-accent,#F77F11)] transition-[width] duration-500" style={{ width: `${basePct}%` }} />
            {sel && (
              <div className="h-full transition-[width] duration-500" data-testid="kx-effect-boost-segment"
                style={{ width: `${100 - basePct}%`, background: 'repeating-linear-gradient(45deg, var(--rest-accent, #F77F11), var(--rest-accent, #F77F11) 6px, rgba(255,255,255,0.55) 6px, rgba(255,255,255,0.55) 10px)' }} />
            )}
          </div>
        </div>
        <div data-testid="kx-effect-without">
          <div className="flex items-center justify-between text-[12px] mb-1">
            <span className="text-gray-500">{isEs ? 'Sin Kitxens (proyección al ritmo anterior)' : 'Without Kitxens (projected at the old pace)'}</span>
            <b className="tabular-nums text-gray-500">{money(d.without_kitxens)}</b>
          </div>
          <div className="h-3.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full rounded-full bg-gray-300 transition-[width] duration-500" style={{ width: `${woPct}%` }} />
          </div>
        </div>
      </div>
      {d.years?.projection?.length > 0 && (() => {
        const yBoost = sel ? (sel.extra_monthly || 0) * 12 : 0;
        const baseY = d.years.baseline_yearly || 0;
        const proj = d.years.projection;
        const maxEx = (proj[proj.length - 1]?.extra || 1) + yBoost;
        return (
          <div className="mt-5" data-testid="kx-years">
            <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-gray-400">
              {isEs ? 'Así se acumula el efecto, año a año' : 'How the effect compounds, year by year'}
              {sel && <span className="ml-2 normal-case tracking-normal text-[var(--rest-accent,#F77F11)]">{isEs ? `· con +$${sel.budget}/mes` : `· with +$${sel.budget}/mo`}</span>}
            </p>
            <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {proj.map((y) => {
                const ex = y.extra + yBoost;
                const up = baseY ? Math.round(1000 * ex / baseY) / 10 : y.uplift_pct;
                const cum = y.cumulative_extra + yBoost * y.year;
                return (
                  <div key={y.year} className="rounded-xl border border-gray-200 bg-gray-50/60 p-3" data-testid={`kx-year-${y.year}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-bold text-gray-500">{isEs ? 'Año' : 'Year'} {y.year}</p>
                      <span className="text-[10.5px] font-bold text-emerald-600 tabular-nums">+{up}%</span>
                    </div>
                    <p className="mt-1 text-[15px] font-black tabular-nums text-gray-900">+{money(ex)}</p>
                    {sel && (
                      <p className="text-[10px] font-semibold text-[var(--rest-accent,#F77F11)] tabular-nums" data-testid={`kx-year-${y.year}-boost`}>
                        {isEs ? `incluye +${money(yBoost)} por presupuesto` : `includes +${money(yBoost)} from budget`}
                      </p>
                    )}
                    <div className="mt-1.5 h-2 rounded-full bg-gray-200/70 overflow-hidden flex">
                      <div className="h-full bg-[var(--rest-accent,#F77F11)] transition-[width] duration-500"
                        style={{ width: `${Math.min(100, 100 * y.extra / maxEx)}%` }} />
                      {sel && (
                        <div className="h-full transition-[width] duration-500"
                          style={{ width: `${Math.min(100, 100 * yBoost / maxEx)}%`, background: 'repeating-linear-gradient(45deg, var(--rest-accent, #F77F11), var(--rest-accent, #F77F11) 5px, rgba(255,255,255,0.55) 5px, rgba(255,255,255,0.55) 8px)' }} />
                      )}
                    </div>
                    <p className="mt-1 text-[10px] text-gray-400 tabular-nums">{isEs ? 'acumulado' : 'cumulative'}: +{money(cum)} <span className="opacity-80">({isEs ? 'estimado' : 'estimated'})</span></p>
                  </div>
                );
              })}
            </div>
            <p className="mt-1.5 text-[10px] text-gray-400">
              {isEs
                ? `Proyección gradual sobre tu base anual, según tu perfil de cocina${sel ? ` — incluye el presupuesto de +$${sel.budget}/mes seleccionado` : ''}. Datos estimados, no garantizados.`
                : `Gradual projection over your yearly baseline, by cuisine profile${sel ? ` — includes the selected +$${sel.budget}/mo budget` : ''}. Estimated data, not guaranteed.`}
            </p>
          </div>
        );
      })()}
      <BudgetSimulator d={d} period={period} isEs={isEs} bi={bi} setBi={setBi} />
      <MethodologyNote isEs={isEs} />
      <p className="mt-4 text-[11px] text-gray-400">
        {isEs
          ? `Base (${scopeTxt.toLowerCase()}): promedio diario pre-Kitxens (${money(d.weekly_without)}/semana) vs ritmo actual (${money(d.weekly_with)}/semana) · ${d.days_tracked} días de datos reales · Todo estimado, sin garantías.`
          : `Baseline (${scopeTxt.toLowerCase()}): pre-Kitxens daily average (${money(d.weekly_without)}/week) vs current pace (${money(d.weekly_with)}/week) · ${d.days_tracked} days of real data · All estimated, not guaranteed.`}
      </p>
    </div>
  );
}
